/**
 * Phase 3x — subscribe to the native streaming speaker classifier.
 *
 * The Swift plugin dispatches a `phase3x-label` CustomEvent on window
 * every 200ms while a session is recording. This hook listens for
 * those events, filters by session id, and exposes the most recent
 * label to any component that wants to render a live "who's speaking"
 * chip.
 *
 * We use a DOM CustomEvent rather than Tauri's addPluginListener
 * because Tauri's listener system requires a `register_listener`
 * command on the Rust plugin side that custom plugins don't ship by
 * default. Direct DOM dispatch from Swift's webview.evaluateJavaScript
 * sidesteps that entirely.
 *
 * The label is a preview — authoritative diarization runs after
 * `stopSession` and rewrites the session's turns. Consumers should
 * treat this as UI fuel, not a source of truth.
 */
import { useEffect, useState } from "react";

export interface LiveSpeakerLabel {
  sessionId: string;
  startSeconds: number;
  endSeconds: number;
  /** "user_self" | contact voiceprint id | "unknown" */
  stateKey: string;
  contactId: string | null;
  cosine: number;
}

export function useLiveSpeakerLabel(activeSessionId: string | null): LiveSpeakerLabel | null {
  const [label, setLabel] = useState<LiveSpeakerLabel | null>(null);

  useEffect(() => {
    setLabel(null);

    if (!activeSessionId) return;

    const handler = (event: Event) => {
      const detail = (event as CustomEvent<LiveSpeakerLabel>).detail;
      if (!detail || detail.sessionId !== activeSessionId) return;
      setLabel(detail);
    };

    window.addEventListener("phase3x-label", handler);
    return () => {
      window.removeEventListener("phase3x-label", handler);
    };
  }, [activeSessionId]);

  return label;
}
